import type {
  IInitialFilter,
  ISearchParameters,
} from '@features/filters/types';
import type { ICampaignActivitySearch } from '@modules/campaignActivityReport/types';

import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { getInitialSearchParameters } from '@modules/campaignActivityReport/utils/getInitialSearchParameters';

interface IInitialState {
  isAllRowsExpanded: boolean;
  searchParameters: ISearchParameters;
  isReturnedFromDetailedView: boolean;
  expandedRows: Record<string, boolean>;
  prevFilters: Record<string, IInitialFilter>;
}

const initialState: IInitialState = {
  prevFilters: {},
  expandedRows: {},
  isAllRowsExpanded: false,
  isReturnedFromDetailedView: false,
  searchParameters: getInitialSearchParameters(),
};

const campaignActivitySlice = createSlice({
  initialState,
  name: 'campaignActivitySlice',
  reducers: {
    resetExpandedRows(state: IInitialState) {
      state.expandedRows = initialState.expandedRows;
    },
    setIsAllRowsExpanded(state: IInitialState, action: PayloadAction<boolean>) {
      state.isAllRowsExpanded = action.payload;
    },
    setPrevFilters(
      state: IInitialState,
      action: PayloadAction<Record<string, IInitialFilter>>
    ) {
      state.prevFilters = action.payload;
    },
    setSearchParameters(
      state: IInitialState,
      action: PayloadAction<Partial<ISearchParameters>>
    ) {
      state.searchParameters = { ...state.searchParameters, ...action.payload };
    },
    setIsReturnedFromDetailedView(
      state: IInitialState,
      action: PayloadAction<IInitialState['isReturnedFromDetailedView']>
    ) {
      state.isReturnedFromDetailedView = action.payload;
    },
    updateExpandedRows(
      state: IInitialState,
      action: PayloadAction<Record<string, boolean>>
    ) {
      state.expandedRows = {
        ...state.expandedRows,
        ...action.payload,
      };
    },
    updateExpandedRowsWithSearchResponse(
      state: IInitialState,
      action: PayloadAction<ICampaignActivitySearch>
    ) {
      if (state.isAllRowsExpanded) {
        const rowIds = action.payload.data?.map(({ id }) => id) || [];
        const updatedConfig = Object.fromEntries(
          rowIds.map((id) => [id, true])
        );

        state.expandedRows = {
          ...state.expandedRows,
          ...updatedConfig,
        };
      }
    },
  },
});

export default campaignActivitySlice.reducer;

export const {
  setPrevFilters,
  resetExpandedRows,
  updateExpandedRows,
  setSearchParameters,
  setIsAllRowsExpanded,
  setIsReturnedFromDetailedView,
  updateExpandedRowsWithSearchResponse,
} = campaignActivitySlice.actions;
